import React, { forwardRef } from 'react';
import { CardProps } from '../../../types';
import { cn } from '../../../utils/cn';

/**
 * Card component for containing content in a visually distinct container
 *
 * Usage:
 * ```tsx
 * <Card>
 *   <h2>Card Title</h2>
 *   <p>Card content goes here</p>
 * </Card>
 *
 * <Card variant="elevated" hoverable padding="large">
 *   Content with elevation, hover effect, and large padding
 * </Card>
 * ```
 */
const Card = forwardRef<HTMLDivElement, CardProps>(({
  as: Component = 'div',
  children,
  className,
  hoverable = false,
  padding = 'medium',
  variant = 'default',
  ...rest
}, ref) => {
  // Base style classes
  const baseClasses = 'rounded-lg transition-all';

  // Variant classes
  const variantClasses = {
    default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
    outline: 'border border-gray-200 dark:border-gray-700',
    elevated: 'bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/30',
  };

  // Padding classes
  const paddingClasses = {
    none: '',
    small: 'p-3',
    medium: 'p-4',
    large: 'p-6',
  };

  // Hoverable classes
  const hoverableClass = hoverable
    ? 'hover:shadow-lg hover:-translate-y-1 transition-all duration-200'
    : '';

  return (
    <Component
      ref={ref}
      className={cn(
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        hoverableClass,
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

Card.displayName = 'Card';

export default Card;
